/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Mousewheel, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Box } from '@mui/material';
import {
  useEMDR,
  useMainParticipant,
  useRoomHost,
  useParticipantsContext,
  useScreenShareParticipant,
  useScreenSize,
  useVideo,
  useWhiteboard,
} from 'hooks';
import { ChevronRightIcon } from 'assets';
import { Participant } from '../Participant';
import { IEMDRContext, IWhiteboardContext } from 'context';

import styles from './ParticipantList.module.scss';

type ParticipantListProps = {
  className?: string;
};

export function ParticipantList({ className = '' }: ParticipantListProps) {
  const { room } = useVideo();
  const { speakerViewParticipants } = useParticipantsContext();
  const screenShareParticipant = useScreenShareParticipant();
  const mainParticipant = useMainParticipant();
  const { isHost } = useRoomHost();
  const { isDesktop, isMobileHorizontal, isBetweenMdAndLg, isTablet } = useScreenSize();
  const localParticipant = room!.localParticipant;
  const { state: emdrState }: IEMDRContext = useEMDR();
  const { state: whiteboardState }: IWhiteboardContext = useWhiteboard();

  const navigationPrevRef = useRef<HTMLElement>(null);
  const navigationNextRef = useRef(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [showHideClass, setShowHideClass] = useState<boolean>(false);

  const speakerViewParticipantsToShow = useMemo(() => {
    // show all participants in this cases lsit:
    // 1. if someone share screen
    // 2. or if whiteboard
    // show all participants except main participant in all other cases
    return speakerViewParticipants.filter(
      (participant) =>
        screenShareParticipant ||
        whiteboardState !== 'off' ||
        (!screenShareParticipant && participant !== mainParticipant),
    );
  }, [speakerViewParticipants, screenShareParticipant, whiteboardState, mainParticipant]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (navigationPrevRef.current) {
        setShowHideClass(true);
        clearInterval(interval);
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (emdrState !== 'off') {
      return;
    }

    const swiper = swiperRef.current;
    if (!swiper) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      swiper.update();
      swiper.slideTo(0, 0);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [emdrState]);

  return speakerViewParticipants.length ? (
    <Box
      className={classnames(styles.container, className, {
        [styles.containerHideNav]: showHideClass,
        [styles.hideParticipantList]: (isBetweenMdAndLg || isTablet) && emdrState !== 'off' && isHost,
      })}
    >
      <Swiper
        direction={isDesktop || isMobileHorizontal ? 'vertical' : 'horizontal'}
        navigation={{
          enabled: true,
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
          hideOnClick: false,
          disabledClass: styles.navigationDisabled,
          hiddenClass: styles.navigationHidden,
          lockClass: styles.navigationLock,
          navigationDisabledClass: styles.navigationDisabledClass,
        }}
        modules={[Mousewheel, Navigation]}
        allowTouchMove={!isDesktop}
        mousewheel={true}
        slidesPerView='auto'
        centeredSlides={false}
        spaceBetween={10}
        className={styles.swiper}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
      >
        <SwiperSlide className={styles.slide}>
          <Participant participant={localParticipant} isLocalParticipant={true} withBorder showSelfViewToggle />
        </SwiperSlide>

        {speakerViewParticipantsToShow.map((participant) => (
          <SwiperSlide className={styles.slide} key={participant.sid}>
            <Participant participant={participant} withBorder />
          </SwiperSlide>
        ))}
      </Swiper>

      <Box
        slot='container-start'
        ref={navigationPrevRef}
        className={classnames(styles.navigationPrevEl, styles.navigationEl)}
      >
        <ChevronRightIcon sx={{ color: 'var(--app-text-muted)', fontSize: '2rem', rotate: '90deg' }} />
      </Box>
      <Box
        slot='container-end'
        ref={navigationNextRef}
        className={classnames(styles.navigationNextEl, styles.navigationEl)}
      >
        <ChevronRightIcon sx={{ color: 'var(--app-text-muted)', fontSize: '2rem', rotate: '-90deg' }} />
      </Box>
    </Box>
  ) : null;
}
